// IndexedDB durable storage adapter (default for web/PWA and Capacitor WebView).
// Implements the shared storage contract: put/get/all/del over named key-value stores.
// NEVER store passwords/JWT/API secrets here. Records are namespaced per user upstream.

export const DB_NAME = "caterctrl_offline";
export const DB_VERSION = 1;
export const STORES = ["outbox", "refcache", "meta"];

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      // Explicit schema/migrations keyed by DB_VERSION.
      for (const s of STORES) {
        if (!db.objectStoreNames.contains(s)) db.createObjectStore(s, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Factory so tests (and future callers) can obtain an isolated adapter instance.
export function createIndexedDbStorage() {
  async function tx(store, mode, fn) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const t = db.transaction(store, mode);
      const os = t.objectStore(store);
      const out = fn(os);
      t.oncomplete = () => resolve(out && out.result !== undefined ? out.result : out);
      t.onerror = () => reject(t.error);
    });
  }
  return {
    kind: "indexeddb",
    async put(store, key, value) {
      return tx(store, "readwrite", (os) => os.put({ key, value }));
    },
    async get(store, key) {
      const db = await openDb();
      return new Promise((resolve, reject) => {
        const r = db.transaction(store, "readonly").objectStore(store).get(key);
        r.onsuccess = () => resolve(r.result ? r.result.value : null);
        r.onerror = () => reject(r.error);
      });
    },
    async all(store) {
      const db = await openDb();
      return new Promise((resolve, reject) => {
        const r = db.transaction(store, "readonly").objectStore(store).getAll();
        r.onsuccess = () => resolve((r.result || []).map((x) => x.value));
        r.onerror = () => reject(r.error);
      });
    },
    async del(store, key) {
      return tx(store, "readwrite", (os) => os.delete(key));
    },
  };
}
